import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const statusLabel = {
  collected: 'เก็บแล้ว',
  used: 'ใช้แล้ว'
};

const UserCoupons = ({ language = 'th' }) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'collected' | 'used'

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/collections/my`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCollections(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load collections', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === 'all' ? collections : collections.filter(c => c.collection_status === filter);

  const formatDiscount = (c) =>
    c.discount_type === 'percentage' ? `${c.discount_value}%` : `${c.discount_value} บาท`;

  const isExpired = (c) => c.valid_until && new Date(c.valid_until) < new Date();

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>กำลังโหลด...</div>;
  }

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['all', 'ทั้งหมด'], ['collected', 'เก็บแล้ว'], ['used', 'ใช้แล้ว']].map(([val, label]) => (
          <button
            key={val}
            onClick={() => setFilter(val)}
            style={{
              padding: '6px 16px',
              borderRadius: 20,
              border: '1.5px solid',
              borderColor: filter === val ? '#f39c12' : '#ddd',
              background: filter === val ? '#f39c12' : '#fff',
              color: filter === val ? '#fff' : '#555',
              fontWeight: filter === val ? 600 : 400,
              cursor: 'pointer',
              fontSize: 13
            }}
          >
            {label} {val === 'all' ? `(${collections.length})` : `(${collections.filter(c => c.collection_status === val).length})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>
          ไม่มีคูปอง
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {filtered.map(c => {
            const expired = isExpired(c);
            const cancelled = c.status === 'cancelled';
            const used = c.collection_status === 'used';
            const dimmed = expired || cancelled || used;

            return (
              <div
                key={c.collection_id}
                style={{
                  border: `2px solid ${dimmed ? '#ddd' : '#f39c12'}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                  opacity: dimmed ? 0.65 : 1,
                  background: dimmed ? '#f9f9f9' : '#fff',
                  boxShadow: dimmed ? 'none' : '0 2px 8px rgba(243,156,18,0.15)'
                }}
              >
                {/* Orange header strip */}
                <div style={{
                  background: dimmed ? '#bbb' : 'linear-gradient(135deg, #f39c12, #e67e22)',
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 22 }}>
                    {formatDiscount(c)}
                  </span>
                  <span style={{
                    background: 'rgba(255,255,255,0.25)',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 10
                  }}>
                    {cancelled ? 'ยกเลิกแล้ว' : used ? 'ใช้แล้ว' : expired ? 'หมดอายุ' : statusLabel[c.collection_status] || c.collection_status}
                  </span>
                </div>

                {/* Body */}
                <div style={{ padding: '12px 16px' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                    {c.title_th || c.title || 'คูปอง'}
                  </div>
                  {c.title && c.title !== c.title_th && (
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{c.title}</div>
                  )}
                  {c.coupon_code && (
                    <div style={{
                      fontFamily: 'monospace',
                      letterSpacing: 3,
                      fontSize: 15,
                      background: '#f5f5f5',
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 4,
                      marginBottom: 6,
                      fontWeight: 700
                    }}>
                      {c.coupon_code}
                    </div>
                  )}
                  {c.discount_type === 'percentage' && c.max_discount_amount && (
                    <div style={{ fontSize: 12, color: '#888' }}>ลดสูงสุด {c.max_discount_amount} บาท</div>
                  )}
                  {c.valid_until && (
                    <div style={{ fontSize: 12, color: expired ? '#e74c3c' : '#888', marginTop: 4 }}>
                      หมดอายุ: {new Date(c.valid_until).toLocaleDateString('th-TH')}
                    </div>
                  )}
                  {c.terms_th && (
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 6, borderTop: '1px solid #f0f0f0', paddingTop: 6 }}>
                      {c.terms_th}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UserCoupons;
